import {
  Box,
  Chip,
  Stack,
  Typography,
  Button,
  Tooltip,
  TextField,
  Checkbox,
  createFilterOptions,
} from "@mui/material";
import React, { useMemo } from "react";
import {
  DeleteModal,
  InputControl,
  LimitedTag,
  PageListLayout,
  ServerPaginatedtable,
  SimpleMenu,
} from "../../../shared";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useTipsAndTricks } from "../hooks/useTipsAndTricks";
import AddIcon from "@mui/icons-material/Add";

export const TipsAndTricksList = () => {
  const navigate = useNavigate();
  const {
    setPagination,
    pagination,
    sorting,
    setSorting,
    listQuery,
    onSearchHandler,
    onDelete,
    confirmDelete,
    resetDeleteInfo,
    deleteInfo,
    isCreateVisible,
    isEditable,
    isDeletable,
    moduleName,
    search,
    onChangeStatusHandler,
    selectTypeOptions,
    tableData,
    categoryValuesHandler,
    CategoryOptions,
    onClearFilter,
    disableclearButton,
  } = useTipsAndTricks({
    isList: true,
    isRecipeTipsList: true,
  });
  const columnHelper = createColumnHelper([]);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.name,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ row }) => {
          return (
            <Box
              sx={{
                wordBreak: "break-all", // Break long words
                overflow: "hidden", // Hide overflow
                maxWidth: "300px", // Set a max width to see the effect
              }}
            >
              {row.original.name}
            </Box>
          );
        },
        meta: { minWidth: "150px" },
      }),
      columnHelper.accessor("type", {
        header: "Type",
        meta: { minWidth: "100px" },
      }),

      columnHelper.accessor("categories", {
        header: "Categories",
        enableSorting: false,
        meta: { minWidth: "150px" },
        cell: ({ row }) => {
          return row?.original?.categories?.length ? (
            <Stack gap="8px" flexDirection="row" flexWrap="wrap">
              {row?.original?.categories?.slice(0, 1).map((data) => (
                <Chip
                  size="small"
                  variant="outlined"
                  color="gray"
                  label={data.category_name}
                />
              ))}
              {row?.original?.categories?.length > 1 && (
                <Tooltip
                  placement="bottom"
                  arrow
                  title={
                    <>
                      {row?.original?.categories
                        ?.slice(1, row?.original?.categories?.length)
                        .map((val, index) => (
                          <div key={index}>{val.category_name}</div>
                        ))}
                    </>
                  }
                >
                  <Chip
                    label={`+${row?.original?.categories?.length - 1} More`}
                    variant="more"
                    size="small"
                  />
                </Tooltip>
              )}
            </Stack>
          ) : (
            <Typography color="#E79A53" fontWeight={500} fontSize="14px">
              No Category Tagged
            </Typography>
          );
        },
      }),

      columnHelper.accessor("recipes", {
        header: "Recipes",
        cell: ({ row }) => {
          let recipes = row.original.recipes;
          return recipes.length ? (
            <Stack gap="8px" flexDirection="row" flexWrap="wrap">
              {recipes?.slice(0, 8).map((data, i) => {
                return (
                  <Chip
                    key={i}
                    label={data.recipe_name}
                    variant="outlined"
                    size="small"
                    color="gray"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(
                        `/recipe/details/${data?.recipe_id}/?basic-details`
                      );
                    }}
                  />
                );
              })}
              {recipes.length > 8 && (
                <Tooltip
                  placement="bottom"
                  arrow
                  title={recipes
                    ?.slice(8, recipes?.length)
                    .map((val) => {
                      return val.recipe_name;
                    })
                    .join(", ")}
                >
                  <Chip
                    label={
                      `+${recipes?.length - recipes?.slice(0, 8)?.length}` +
                      " More"
                    }
                    variant="more"
                    size="small"
                  />
                </Tooltip>
              )}
            </Stack>
          ) : (
            <Typography color="#E79A53" fontWeight={500} fontSize="14px">
              No Recipe Tagged
            </Typography>
          );
        },
        enableSorting: false,
      }),

      columnHelper.display({
        id: "actions",
        cell: (props) => {
          const { row } = props;
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap="8px"
            >
              {(isEditable || isDeletable) && (
                <SimpleMenu
                  row={props.row}
                  editAction={(e) => navigate(`edit/${e.id}`)}
                  handleDeleteModal={(e) => onDelete(e.id)}
                  isEditable={isEditable}
                  isDeletable={isDeletable}
                />
              )}

              <img alt="details" src="/icons/arrow_forward.png" />
            </Stack>
          );
        },
      }),
    ],
    []
  );

  const breadcrumbs = [
    <Typography key="1" sx={{ fontSize: "14px", fontWeight: 400 }}>
      Tips & Procedures
    </Typography>,
  ];

  return (
    <PageListLayout breadcrumbs={breadcrumbs} sx={{ p: 0 }}>
      <ServerPaginatedtable
        defaultData={listQuery?.data?.tipsandtricks ?? []}
        columns={columns}
        onSearchChange={onSearchHandler}
        search={search}
        sorting={sorting}
        pagination={pagination}
        onRowLink={(e) => {
          return `details/${e.id}?basic-details`;
        }}
        paginationProps={
          listQuery.data?.pagination || { total: 0, lastPage: 1 }
        }
        setSorting={(callback) =>
          setSorting({ tableName: moduleName, callback })
        }
        setPagination={(callback) =>
          setPagination({ tableName: moduleName, callback })
        }
        headerText="Tips & Procedures"
        isLoading={listQuery?.isLoading}
        tableHeaderContent={
          <Stack
            sx={{
              flexDirection: "row",
              gap: "6px",
              width: "100%",
            }}
          >
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              options={selectTypeOptions}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              sx={{
                width: "180px",
                height: "36px",
              }}
              value={
                (selectTypeOptions &&
                  tableData?.tipsAndTricks?.data?.selectedType?.value &&
                  selectTypeOptions?.find(
                    (status) =>
                      status?.value ===
                      tableData?.tipsAndTricks?.data?.selectedType?.value
                  )) ||
                null
              }
              onChange={(event, option) => onChangeStatusHandler(option)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Type" />
              )}
            />
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              fullWidth
              multiple={true}
              options={CategoryOptions ?? []}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              sx={{
                width: "250px",
              }}
              renderTags={(value) => (
                <LimitedTag value={value} autocompleteWidth="250px" />
              )}
              value={tableData?.tipsAndTricks?.data?.categoryfilter ?? []}
              onChange={(event, option) => categoryValuesHandler(event, option)}
              renderOption={(props, option, { selected }) => (
                <>
                  <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option?.name}
                  </li>
                </>
              )}
              filterOptions={filterOptions}
              renderInput={(params) => (
                <TextField {...params} placeholder="Category" />
              )}
            />

            <Button
              type="button"
              size="small"
              variant="text"
              sx={{
                p: "6px 12px 6px 12px",
                gap: "6px",
              }}
              disabled={disableclearButton}
              onClick={onClearFilter}
            >
              Clear All
            </Button>
          </Stack>
        }
        rightContent={
          <Stack direction="row" gap="8px">
            {isCreateVisible && (
              <Button
                type="button"
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("create")}
              >
                Add
              </Button>
            )}
          </Stack>
        }
      />
      <DeleteModal
        open={deleteInfo.show}
        handleClose={() => resetDeleteInfo()}
        onConfirm={() => confirmDelete()}
      />
    </PageListLayout>
  );
};
